import React from 'react'
import { z } from 'zod'

import { Logo } from '../Logo'
import { Button } from '../../catalyst/button'
import { TextField, useNativeFormSubmit, useValidatedForm } from '../forms'

interface AcceptInvitationProps {
  invitation_token: string
  minimum_password_length?: number
  errors?: string[]
  submit_url: string
  csrf_token: string
}

const DEFAULT_MINIMUM_PASSWORD_LENGTH = 6

// Built per render because the minimum length arrives as a prop; nested to
// mirror the params hash Rails receives.
const buildSchema = (minimumLength: number) =>
  z
    .object({
      user: z.object({
        password: z
          .string()
          .min(1, 'Password is required.')
          .min(minimumLength, `Password must be at least ${minimumLength} characters.`),
        password_confirmation: z.string().min(1, 'Please confirm your password.'),
      }),
    })
    .superRefine((data, ctx) => {
      const { password, password_confirmation: confirmation } = data.user
      if (confirmation && confirmation !== password) {
        ctx.addIssue({
          code: 'custom',
          message: 'Passwords do not match.',
          path: ['user', 'password_confirmation'],
        })
      }
    })

type InvitationValues = z.infer<ReturnType<typeof buildSchema>>

export function AcceptInvitation({
  invitation_token,
  minimum_password_length,
  errors = [],
  submit_url,
  csrf_token,
}: AcceptInvitationProps) {
  const minimumLength = minimum_password_length ?? DEFAULT_MINIMUM_PASSWORD_LENGTH
  const form = useValidatedForm<InvitationValues>(
    React.useMemo(() => buildSchema(minimumLength), [minimumLength]),
    { defaultValues: { user: { password: '', password_confirmation: '' } } },
  )
  const formProps = useNativeFormSubmit(form)

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Logo className="mx-auto h-24 w-auto fill-[color:var(--theme-accent)]" />
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-[color:var(--theme-fg)]">
            Welcome!
          </h2>
          <p className="mt-2 text-sm text-[color:var(--theme-muted)]">
            Set your password to activate your account
          </p>
        </div>

        <form action={submit_url} method="post" className="mt-8 space-y-6" {...formProps}>
          <input type="hidden" name="authenticity_token" value={csrf_token} />
          <input type="hidden" name="_method" value="put" />
          <input type="hidden" name="user[invitation_token]" value={invitation_token} />

          <div className="space-y-4">
            <TextField
              form={form}
              name="user.password"
              submitName="user[password]"
              label="Password"
              type="password"
              autoComplete="new-password"
              autoFocus
              description={`Minimum ${minimumLength} characters.`}
            />

            <TextField
              form={form}
              name="user.password_confirmation"
              submitName="user[password_confirmation]"
              label="Confirm Password"
              type="password"
              autoComplete="new-password"
            />
          </div>

          {errors.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 dark:bg-red-950 p-4 ring-1 ring-red-200 dark:ring-red-800"
            >
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                {errors.length} {errors.length === 1 ? 'error' : 'errors'} prohibited this from
                being saved:
              </h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700 dark:text-red-300">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <Button type="submit" className="w-full">
            Set Password & Activate Account
          </Button>
        </form>
      </div>
    </div>
  )
}
